import { Modal, Button } from '@orbita-ui/core';
import { useState, useContext } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Card } from 'shared/components';
import { PageTitle } from 'shared/components/Fonts';
import { useNavigate } from 'react-router-dom';
import { useCoreService } from 'core/services/useCoreService';
import { BaseModal, useModal } from 'shared/modal';
import { CardsFiltersForm } from '../../components/CardsFiltersForm/CardsFiltersForm';
import { CardsTableWithPagination } from 'core/tarjeta/components/CardsTableWithPagination/CardsTableWithPagination';
import { Tarjeta } from '../../models/Tarjeta';
import { CARD_MESSAGES } from 'shared/constants/CardMessages';
import { useNotifications } from '../../../../shared/notifications';
import { ASSOCIATE_CARD_PATH } from '../../routes';
import { SearchTarjetaRequest } from 'core/tarjeta/models/SearchTarjetaRequest';
import { CardListContext } from 'core/tarjeta/contexts/CardListContext';

interface ModalMessages {
  disassociate: (cardNumber: string) => string;
  change: (cardNumber: string) => string;
  associate: (cardNumber: string) => string;
}

const MODAL_MESSAGES: ModalMessages = {
  disassociate: CARD_MESSAGES.MODAL.DISASSOCIATE_WITH_BALANCE,
  change: CARD_MESSAGES.MODAL.CHANGE_CC_WITH_BALANCE,
  associate: CARD_MESSAGES.MODAL.ASSOCIATE_WITH_BALANCE,
};

type ActionType = 'disassociate' | 'change' | 'associate';

export function CardsListPage() {
  const { searchParams, searchCards, reloadToken, reloadCardsAndFilters } =
    useContext(CardListContext);

  const navigate = useNavigate();
  const service = useCoreService();
  const { addNotification } = useNotifications();

  const [isDisassociating, setIsDisassociating] = useState<boolean>(false);

  const {
    showModal: showErrorModal,
    closeModal: closeErrorModal,
    openModal: openErrorModal,
  } = useModal(false);
  const {
    showModal: showConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
  } = useModal(false);
  const {
    showModal: showSharedAccountWarningModal,
    closeModal: closeSharedAccountWarningModal,
    openModal: openSharedAccountWarningModal,
  } = useModal(false);

  const [selectedCardForModal, setSelectedCardForModal] = useState<Tarjeta | null>(null);
  const [otherCards, setOtherCards] = useState<any[]>([]);

  const [selectedActionType, setSelectedActionType] = useState<ActionType | null>(null);

  const handleSearch = async (filters: SearchTarjetaRequest): Promise<void> => {
    searchCards(filters);
  };

  const handleAsociarCard = async (card: Tarjeta): Promise<void> => {
    setSelectedCardForModal(card);
    setSelectedActionType('associate');

    const saldoResponse = await service.getAccountSaldo(card.numeroCuenta);

    if (saldoResponse.body.tieneSaldoDisponible) {
      openErrorModal();
    } else {
      const otherCards =
        saldoResponse.body.tarjetas?.filter(
          (c: any) => c.numero !== card.numero.slice(-4),
        ) || [];

      if (otherCards.length > 0) {
        setOtherCards(otherCards);
        openSharedAccountWarningModal();
      } else {
        proceedWithAction('associate', card);
      }
    }
  };

  const handleDisassociateConfirm = async () => {
    if (!selectedCardForModal) return;

    setIsDisassociating(true);
    try {
      await service.disassociateCard(selectedCardForModal.id);
      addNotification(
        CARD_MESSAGES.NOTIFICATIONS.DISASSOCIATE_SUCCESS(selectedCardForModal.numero),
        'success',
      );
      closeConfirmModal();
      reloadCardsAndFilters();
    } catch (error) {
      addNotification(CARD_MESSAGES.NOTIFICATIONS.DISASSOCIATE_ERROR, 'error');
    } finally {
      setIsDisassociating(false);
      setSelectedCardForModal(null);
    }
  };

  const handleCardAction = async (
    nroCuenta: string,
    card: Tarjeta,
    actionType: 'disassociate' | 'change',
  ): Promise<void> => {
    if (!card) return;

    setSelectedCardForModal(card);
    setSelectedActionType(actionType);

    const saldoResponse = await service.getAccountSaldo(nroCuenta);

    if (saldoResponse.body.tieneSaldoDisponible) {
      openErrorModal();
    } else {
      const otherCards =
        saldoResponse.body.tarjetas?.filter(
          (card: any) => card.numero !== card.numero.slice(-4),
        ) || [];

      setOtherCards(otherCards);

      if (actionType === 'disassociate') {
        openConfirmModal();
      } else if (actionType === 'change') {
        if (otherCards.length > 0) {
          openSharedAccountWarningModal();
        } else {
          proceedWithAction('change', card);
        }
      }
    }
  };

  const proceedWithAction = (actionType: ActionType | null, card: Tarjeta) => {
    if (actionType === 'disassociate') {
      openConfirmModal();
    } else if (actionType === 'change' || actionType === 'associate') {
      navigate(ASSOCIATE_CARD_PATH, { state: { cardData: card } });
    }
  };

  const handleSharedAccountConfirm = () => {
    if (!selectedCardForModal) return;
    closeSharedAccountWarningModal();
    proceedWithAction(selectedActionType, selectedCardForModal);
  };

  return (
    <Container fluid>
      <Row className="mb-3 d-flex justify-content-between align-items-center">
        <Col>
          <PageTitle>Lista de Tarjetas</PageTitle>
        </Col>
        {/* <Col className="text-end">
          <Button
            color="primary"
            icon="ChainIcon"
            type="button"
            onClick={() => navigate('#')}
          >
            Asociar Tarjeta con Archivo
          </Button>
        </Col> */}
      </Row>

      <Row className="mb-4">
        <Col>
          <CardsFiltersForm onSearch={handleSearch} initialFilters={searchParams} />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Title>Tarjetas</Card.Title>
            <Card.Body>
              <CardsTableWithPagination
                searchParams={searchParams}
                onChangeAction={(nroCuenta, cardId) =>
                  handleCardAction(nroCuenta, cardId, 'change')
                }
                onDisassociateAction={(nroCuenta, cardId) =>
                  handleCardAction(nroCuenta, cardId, 'disassociate')
                }
                onAssociateAction={handleAsociarCard}
                reloadToken={reloadToken}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CardActionModal
        show={showErrorModal}
        onClose={closeErrorModal}
        actionType={selectedActionType}
        cardNumber={selectedCardForModal?.numero ?? ''}
        modalMessages={MODAL_MESSAGES}
      />

      {selectedCardForModal && (
        <DisassociateConfirmationModal
          show={showConfirmModal}
          onClose={closeConfirmModal}
          onConfirm={handleDisassociateConfirm}
          card={selectedCardForModal}
          isLoading={isDisassociating}
          otherCards={otherCards}
        />
      )}

      {selectedCardForModal && (
        <SharedAccountWarningModal
          show={showSharedAccountWarningModal}
          onClose={closeSharedAccountWarningModal}
          onConfirm={handleSharedAccountConfirm}
          actionType={selectedActionType}
          card={selectedCardForModal}
          otherCards={otherCards}
        />
      )}
    </Container>
  );
}

interface CardActionModalProps {
  show: boolean;
  onClose: () => void;
  actionType: ActionType | null;
  cardNumber: string;
  modalMessages: ModalMessages;
}

const CardActionModal = ({
  show,
  onClose,
  actionType,
  cardNumber,
  modalMessages,
}: CardActionModalProps) => {
  const getModalContent = () => {
    switch (actionType) {
      case 'disassociate':
        return {
          title: 'Desasociar tarjeta',
          message: modalMessages.disassociate(cardNumber),
        };
      case 'change':
        return {
          title: 'Cambiar centro de costo',
          message: modalMessages.change(cardNumber),
        };
      case 'associate':
        return {
          title: 'Asociar a centro de costo',
          message: modalMessages.associate(cardNumber),
        };
      default:
        return { title: '', message: '' };
    }
  };

  const { title, message } = getModalContent();

  return (
    <Modal show={show} onClose={onClose}>
      <BaseModal
        title={title}
        body={<p>{message}</p>}
        footer={<Button onClick={onClose}>Aceptar</Button>}
      />
    </Modal>
  );
};

interface DisassociateConfirmationModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  card: Tarjeta;
  isLoading: boolean;
  otherCards: any[];
}
const DisassociateConfirmationModal = ({
  show,
  onClose,
  onConfirm,
  card,
  isLoading,
  otherCards,
}: DisassociateConfirmationModalProps) => {
  const otherCardsText = otherCards.map((c) => c.numero).join(', ');

  return (
    <Modal show={show} onClose={onClose}>
      <BaseModal
        title={CARD_MESSAGES.MODAL.DISASSOCIATE_CONFIRMATION.TITLE}
        body={
          <div>
            <p>
              {CARD_MESSAGES.MODAL.DISASSOCIATE_CONFIRMATION.BODY(
                card.numero,
                card.centroCostoAsociado,
              )}
            </p>
            {otherCards.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <p>
                  Esta tarjeta comparte cuenta con las siguientes tarjetas:{' '}
                  {otherCardsText}
                </p>
                <p>
                  Si continúa, se desasociará también a estas tarjetas del centro de
                  costos.
                </p>
              </div>
            )}
          </div>
        }
        footer={
          <>
            <Button
              className="bg-secondary"
              color="inverted"
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button color="destructive" onClick={onConfirm} loading={isLoading}>
              Desasociar
            </Button>
          </>
        }
      />
    </Modal>
  );
};

interface SharedAccountWarningModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: ActionType | null;
  card: Tarjeta;
  otherCards: any[];
}

const SharedAccountWarningModal = ({
  show,
  onClose,
  onConfirm,
  actionType,
  card,
  otherCards,
}: SharedAccountWarningModalProps) => {
  const getModalText = () => {
    switch (actionType) {
      case 'disassociate':
        return {
          title: 'Desasociar Tarjeta',
          actionText: 'se desasociará el centro de costo para estas tarjetas.',
          confirmButtonText: 'Continuar',
          color: 'primary' as const,
        };
      case 'change':
        return {
          title: 'Cambiar Centro de Costo',
          actionText: 'se cambiará el centro de costo para estas tarjetas.',
          confirmButtonText: 'Continuar',
          color: 'primary' as const,
        };
      case 'associate':
        return {
          title: 'Asociar a Centro de Costo',
          actionText: 'se asociarán al mismo centro de costo que elija.',
          confirmButtonText: 'Continuar',
          color: 'primary' as const,
        };
      default:
        return {
          title: '',
          actionText: '',
          confirmButtonText: 'Aceptar',
          color: 'primary' as const,
        };
    }
  };

  const { title, actionText, confirmButtonText, color } = getModalText();
  const otherCardsText = otherCards.map((c) => c.numero).join(', ');

  return (
    <Modal show={show} onClose={onClose}>
      <BaseModal
        title={title}
        body={
          <div>
            <p>
              La tarjeta {card.numero} comparte la cuenta con las siguientes tarjetas:{' '}
              {otherCardsText}
            </p>
            <p>Si continúa, también {actionText}</p>
          </div>
        }
        footer={
          <>
            <Button
              className="bg-secondary"
              color="inverted"
              variant="ghost"
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button color={color} onClick={onConfirm}>
              {confirmButtonText}
            </Button>
          </>
        }
      />
    </Modal>
  );
};
